import React, { useState, useEffect } from 'react'
import { X, Plus, GripVertical, MoreHorizontal, Edit3, Copy, RotateCw, Settings, Trash2 } from 'lucide-react'

/**
 * Pages View Dialog Component
 * Displays all pages in a grid view with thumbnails and allows page selection
 */
const PagesViewDialog = ({
  showPagesViewDialog,
  setShowPagesViewDialog,
  template_content,
  currentPageIndex,
  setCurrentPageIndex,
  addNewPage,
  settemplate_content
}) => {
  const [draggedIndex, setDraggedIndex] = useState(null)
  const [dragOverIndex, setDragOverIndex] = useState(null)
  const [showPageMenu, setShowPageMenu] = useState(null)
  const [showRenameDialog, setShowRenameDialog] = useState(null)
  const [renameValue, setRenameValue] = useState('')
  const [showResizeDialog, setShowResizeDialog] = useState(null)
  const [resizeWidth, setResizeWidth] = useState('')
  const [resizeHeight, setResizeHeight] = useState('')
  const [showAddPageDialog, setShowAddPageDialog] = useState(false)
  const [newPageName, setNewPageName] = useState('')
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(null)
  const [pageToDelete, setPageToDelete] = useState(null)

  // Close page menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPageMenu !== null && !event.target.closest('.page-menu-container')) {
        setShowPageMenu(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showPageMenu])

  if (!showPagesViewDialog) {
    return null
  }

  const handleDragStart = (e, index) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverIndex(index)
  }

  const handleDragLeave = (e) => {
    // Only clear dragOverIndex if we're leaving the entire page item
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null)
    }
  }

  const handleDrop = (e, dropIndex) => {
    e.preventDefault()

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null)
      setDragOverIndex(null)
      return
    }

    // Reorder pages
    const newPages = [...template_content.pages]
    const draggedPage = newPages[draggedIndex]

    // Remove dragged page
    newPages.splice(draggedIndex, 1)

    // Insert at new position
    newPages.splice(dropIndex, 0, draggedPage)

    // Update template content
    settemplate_content(prev => ({
      ...prev,
      pages: newPages
    }))

    // Update current page index if needed
    if (draggedIndex === currentPageIndex) {
      setCurrentPageIndex(dropIndex)
    } else if (draggedIndex < currentPageIndex && dropIndex >= currentPageIndex) {
      setCurrentPageIndex(currentPageIndex - 1)
    } else if (draggedIndex > currentPageIndex && dropIndex <= currentPageIndex) {
      setCurrentPageIndex(currentPageIndex + 1)
    }

    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  // Page action functions
  const renamePage = (pageIndex, newName) => {
    settemplate_content(prev => ({
      ...prev,
      pages: prev.pages.map((page, index) =>
        index === pageIndex
          ? {
              ...page,
              page_config: {
                ...page.page_config,
                name: newName
              }
            }
          : page
      )
    }))
    setShowRenameDialog(null)
    setRenameValue('')
  }

  const insertPageAfter = (pageIndex) => {
    const currentPage = template_content?.pages?.[pageIndex]
    if (!currentPage) return

    const newPage = {
      id: `page-${Date.now()}`,
      page_config: {
        ...currentPage.page_config,
        name: `Page ${template_content.pages.length + 1}`,
        backgroundColor: '#ffffff'
      },
      items: []
    }

    settemplate_content(prev => ({
      ...prev,
      pages: [
        ...prev.pages.slice(0, pageIndex + 1),
        newPage,
        ...prev.pages.slice(pageIndex + 1)
      ]
    }))
    setShowPageMenu(null)
  }

  const insertPageBefore = (pageIndex) => {
    const currentPage = template_content?.pages?.[pageIndex]
    if (!currentPage) return

    const newPage = {
      id: `page-${Date.now()}`,
      page_config: {
        ...currentPage.page_config,
        name: `Page ${template_content.pages.length + 1}`,
        backgroundColor: '#ffffff'
      },
      items: []
    }

    settemplate_content(prev => ({
      ...prev,
      pages: [
        ...prev.pages.slice(0, pageIndex),
        newPage,
        ...prev.pages.slice(pageIndex)
      ]
    }))
    setShowPageMenu(null)
  }

  const duplicatePage = (pageIndex) => {
    const pageToClone = template_content?.pages?.[pageIndex]
    if (!pageToClone) return

    const newPage = {
      ...pageToClone,
      id: `page-${Date.now()}`,
      page_config: {
        ...pageToClone.page_config,
        name: `${pageToClone.page_config.name} Copy`
      },
      items: pageToClone.items.map(item => ({
        ...item,
        id: `${item.id}-copy-${Date.now()}`,
        layer: item.layer || 0
      }))
    }

    settemplate_content(prev => ({
      ...prev,
      pages: [
        ...prev.pages.slice(0, pageIndex + 1),
        newPage,
        ...prev.pages.slice(pageIndex + 1)
      ]
    }))
    setShowPageMenu(null)
  }

  const resizePage = (pageIndex, width, height) => {
    settemplate_content(prev => ({
      ...prev,
      pages: prev.pages.map((page, index) =>
        index === pageIndex
          ? {
              ...page,
              page_config: {
                ...page.page_config,
                width: parseInt(width),
                height: parseInt(height)
              }
            }
          : page
      )
    }))
    setShowResizeDialog(null)
    setResizeWidth('')
    setResizeHeight('')
  }

  const changeOrientation = (pageIndex) => {
    const currentPage = template_content?.pages?.[pageIndex]
    if (!currentPage) return

    const currentWidth = currentPage.page_config?.width || 800
    const currentHeight = currentPage.page_config?.height || 600

    settemplate_content(prev => ({
      ...prev,
      pages: prev.pages.map((page, index) =>
        index === pageIndex
          ? {
              ...page,
              page_config: {
                ...page.page_config,
                width: currentHeight,
                height: currentWidth
              }
            }
          : page
      )
    }))
    setShowPageMenu(null)
  }

  const deletePage = (pageIndex) => {
    if (template_content?.pages?.length <= 1) {
      alert('Cannot delete the last page!')
      return
    }

    settemplate_content(prev => ({
      ...prev,
      pages: prev.pages.filter((_, index) => index !== pageIndex)
    }))

    // Update current page index if needed
    if (pageIndex === currentPageIndex) {
      setCurrentPageIndex(Math.max(0, pageIndex - 1))
    } else if (pageIndex < currentPageIndex) {
      setCurrentPageIndex(currentPageIndex - 1)
    }

    setShowPageMenu(null)
    setShowDeleteConfirmDialog(null)
    setPageToDelete(null)
  }

  const confirmDeletePage = (pageIndex) => {
    if (template_content?.pages?.length <= 1) {
      alert('Cannot delete the last page!')
      return
    }

    setPageToDelete(pageIndex)
    setShowDeleteConfirmDialog(pageIndex)
    setShowPageMenu(null)
  }

  const addNewPageWithName = (pageName) => {
    const newPage = {
      id: `page-${Date.now()}`,
      page_config: {
        width: 800,
        height: 600,
        name: pageName || `Page ${template_content.pages.length + 1}`,
        backgroundColor: '#ffffff'
      },
      items: []
    }

    settemplate_content(prev => ({
      ...prev,
      pages: [...prev.pages, newPage]
    }))

    setShowAddPageDialog(false)
    setNewPageName('')
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[550] p-4">
      <div className="bg-white rounded-lg w-[800px] max-w-[90vw] max-h-[90vh] flex flex-col">
        <div className="flex-shrink-0 flex items-center justify-between p-6 pb-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">All Pages</h3>
          <button
            onClick={() => setShowPagesViewDialog(false)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-3 gap-4">
          {template_content?.pages?.map((page, index) => (
            <div
              key={page.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              className={`relative cursor-pointer border-2 rounded-lg overflow-hidden transition-all hover:shadow-lg ${
                index === currentPageIndex
                  ? 'border-blue-500 ring-2 ring-blue-200'
                  : 'border-gray-200 hover:border-gray-300'
              } ${
                draggedIndex === index ? 'opacity-50' : ''
              } ${
                dragOverIndex === index && draggedIndex !== index ? 'border-green-400 ring-2 ring-green-200' : ''
              }`}
              onClick={() => {
                if (draggedIndex === null) { // Only allow click if not dragging
                  setCurrentPageIndex(index)
                  // Don't close the dialog - let user close it manually
                }
              }}
            >
              {/* Drag Handle */}
              <div className="absolute top-2 right-2 z-10 p-1 bg-white bg-opacity-80 rounded hover:bg-opacity-100 transition-all">
                <GripVertical className="w-4 h-4 text-gray-500" />
              </div>

              {/* Page Menu Button */}
              <div className="absolute top-2 left-2 z-10">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowPageMenu(showPageMenu === index ? null : index)
                  }}
                  className="p-1 bg-white bg-opacity-80 rounded hover:bg-opacity-100 transition-all"
                  title="Page options"
                >
                  <MoreHorizontal className="w-4 h-4 text-gray-600" />
                </button>

                {/* Page Menu Dropdown */}
                {showPageMenu === index && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50 min-w-48 page-menu-container">
                    {/* Insert Page Before */}
                    <button
                      onClick={() => insertPageBefore(index)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Insert Page Before</span>
                    </button>

                    {/* Insert Page After */}
                    <button
                      onClick={() => insertPageAfter(index)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Insert Page After</span>
                    </button>

                    {/* Icon Buttons Row */}
                    <div className="px-3 py-2 flex items-center justify-center space-x-2">
                      {/* Rename Page */}
                      <button
                        onClick={() => {
                          setRenameValue(page.page_config?.name || `Page ${index + 1}`)
                          setShowRenameDialog(index)
                          setShowPageMenu(null)
                        }}
                        className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                        title="Rename Page"
                      >
                        <Edit3 className="w-4 h-4 text-gray-600" />
                      </button>

                      {/* Duplicate Page */}
                      <button
                        onClick={() => duplicatePage(index)}
                        className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                        title="Duplicate Page"
                      >
                        <Copy className="w-4 h-4 text-gray-600" />
                      </button>

                      {/* Resize Page */}
                      <button
                        onClick={() => {
                          setResizeWidth(page.page_config?.width || 800)
                          setResizeHeight(page.page_config?.height || 600)
                          setShowResizeDialog(index)
                          setShowPageMenu(null)
                        }}
                        className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                        title="Resize Page"
                      >
                        <Settings className="w-4 h-4 text-gray-600" />
                      </button>

                      {/* Delete Page */}
                      {template_content?.pages?.length > 1 && (
                        <button
                          onClick={() => confirmDeletePage(index)}
                          className="p-2 hover:bg-red-50 rounded-md transition-colors"
                          title="Delete Page"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* Page Thumbnail */}
              <div
                className="w-full aspect-[4/3] bg-white flex items-center justify-center relative"
                style={{ backgroundColor: page.page_config?.backgroundColor || '#ffffff' }}
              >
                {/* Mini preview of page content */}
                <div className="absolute inset-2 overflow-hidden">
                  {page.items && page.items.length > 0 ? (
                    page.items.slice(0, 3).map((item, itemIndex) => (
                      <div
                        key={item.id}
                        className="absolute bg-gray-300 rounded opacity-60"
                        style={{
                          left: `${(item.x / (page.page_config?.width || 800)) * 100}%`,
                          top: `${(item.y / (page.page_config?.height || 600)) * 100}%`,
                          width: `${Math.min((item.width / (page.page_config?.width || 800)) * 100, 30)}%`,
                          height: `${Math.min((item.height / (page.page_config?.height || 600)) * 100, 20)}%`,
                        }}
                      />
                    ))
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">
                      <div className="text-center">
                        <div className="text-2xl mb-1">📄</div>
                        <div className="text-xs">Empty</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Current page indicator */}
                {index === currentPageIndex && (
                  <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                    Current
                  </div>
                )}
              </div>

              {/* Page Info */}
              <div className="p-3 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-sm">
                      {page.page_config?.name || `Page ${index + 1}`}
                    </h4>
                    <p className="text-xs text-gray-500">
                      {page.page_config?.width || 800} × {page.page_config?.height || 600}
                    </p>
                  </div>
                  <div className="text-xs text-gray-400">
                    {page.items?.length || 0} items
                  </div>
                </div>
              </div>
            </div>
          ))}
          </div>

          {/* Add New Page Button */}
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => {
                setNewPageName(`Page ${template_content.pages.length + 1}`)
                setShowAddPageDialog(true)
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add New Page</span>
            </button>
          </div>
        </div>
      </div>

      {/* Rename Dialog */}
      {showRenameDialog !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Rename Page</h3>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter page name"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  renamePage(showRenameDialog, renameValue)
                } else if (e.key === 'Escape') {
                  setShowRenameDialog(null)
                  setRenameValue('')
                }
              }}
            />
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => {
                  setShowRenameDialog(null)
                  setRenameValue('')
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => renamePage(showRenameDialog, renameValue)}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                disabled={!renameValue.trim()}
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resize Dialog */}
      {showResizeDialog !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Resize Page</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Width (px)</label>
                <input
                  type="number"
                  value={resizeWidth}
                  onChange={(e) => setResizeWidth(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="800"
                  min="100"
                  max="10000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Height (px)</label>
                <input
                  type="number"
                  value={resizeHeight}
                  onChange={(e) => setResizeHeight(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="600"
                  min="100"
                  max="10000"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => {
                  setShowResizeDialog(null)
                  setResizeWidth('')
                  setResizeHeight('')
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => resizePage(showResizeDialog, resizeWidth, resizeHeight)}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                disabled={!resizeWidth || !resizeHeight || resizeWidth < 100 || resizeHeight < 100}
              >
                Resize
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Page Dialog */}
      {showAddPageDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Add New Page</h3>
            <input
              type="text"
              value={newPageName}
              onChange={(e) => setNewPageName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter page name"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  addNewPageWithName(newPageName)
                } else if (e.key === 'Escape') {
                  setShowAddPageDialog(false)
                  setNewPageName('')
                }
              }}
            />
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => {
                  setShowAddPageDialog(false)
                  setNewPageName('')
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={() => addNewPageWithName(newPageName)}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                disabled={!newPageName.trim()}
              >
                Add Page
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirmDialog !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4 text-red-600">Delete Page</h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete "{template_content?.pages?.[pageToDelete]?.page_config?.name || `Page ${pageToDelete + 1}`}"?
              This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowDeleteConfirmDialog(null)
                  setPageToDelete(null)
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deletePage(pageToDelete)}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete Page
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PagesViewDialog
